/**
 * El mapa de ruta: POL a POD.
 *
 * ---------------------------------------------------------------------------
 * QUE HACE AQUI UN MAPA
 *
 * Un cotizador de carga internacional que no enseña por donde va la carga
 * obliga a leer dos codigos de cinco letras y reconstruir la ruta de memoria.
 * CNSHA -> GTPRQ es Shanghai a Puerto Quetzal para quien lleva años; para quien
 * lleva dos semanas son diez caracteres.
 *
 * BANDA EN LA BUSQUEDA, DOCUMENTO EN LA COTIZACION. La primera version hacia el
 * de la busqueda una franja de 12:1 y la respuesta de quien lo uso fue "funciona
 * pero es muy pequeño": un mapa que hay que acercarse a mirar no ahorra leer los
 * codigos, que era para lo que estaba. Ahora la busqueda lleva una banda ancha
 * sobre los grupos --con los puertos etiquetados, no solo dos puntos-- y la
 * cotizacion un mapa a ancho completo antes del detalle de lineas, porque ese
 * documento lo acaba viendo un cliente.
 *
 * ---------------------------------------------------------------------------
 * LA RUTA CRUZA EL PACIFICO, NO EUROPA
 *
 * Es el punto de este archivo donde un error se ve como una barbaridad y no
 * como un pixel torcido.
 *
 * En un mapa equirectangular de -180 a 180, Shanghai cae a la derecha del todo
 * (x ~ 838) y Valparaiso a la izquierda (x ~ 301). Una linea recta entre los dos
 * atraviesa China, Asia central, Europa y el Atlantico: la vuelta larga. La ruta
 * real sale al Pacifico y cruza el antimeridiano.
 *
 * Se resuelve DESENROLLANDO: cuando la diferencia de longitud pasa de media
 * vuelta, al destino se le suma o resta un ancho de mundo, y a partir de ahi
 * todo --el arco, el encuadre-- se calcula en linea recta sobre esa coordenada
 * corrida. Para que el mapa no se acabe donde acaba el lienzo, la silueta se
 * dibuja tres veces: en su sitio y desplazada un mundo a cada lado. Es el mismo
 * truco de cualquier mapa que se puede arrastrar de lado, y aqui basta con tres
 * copias porque ninguna ruta da mas de una vuelta.
 *
 * ---------------------------------------------------------------------------
 * EL ENCUADRE SE CALCULA, NO ES FIJO
 *
 * La primera version pintaba el mundo entero en una franja de 5 rem y recortaba
 * por el centro: en el navegador se veia Puerto Quetzal y Shanghai quedaba
 * fuera del recorte, o sea un mapa de ruta sin uno de sus dos extremos.
 *
 * Ahora la ventana se calcula sobre la ruta --los dos puertos y la cima del
 * arco-- con margen, y se estira hasta la proporcion que le toca a cada tamaño.
 * Consecuencia buscada: una ruta corta (Shanghai a Hong Kong) se acerca y una
 * transpacifica se aleja, sin que ninguna de las dos se salga.
 *
 * ---------------------------------------------------------------------------
 * LO QUE SE MIDE EN PIXELES Y LO QUE SE MIDE EN MUNDO
 *
 * La geometria --el arco, los puertos, el encuadre-- vive en coordenadas del
 * mundo. Los ADORNOS --el radio de un punto, el cuerpo de una etiqueta-- no:
 * esos se piden en pixeles de pantalla y se convierten a mundo con la escala
 * real, medida sobre el elemento ya montado.
 *
 * Sin eso, el tamaño del texto sale de dividir el lienzo entre la ventana, o
 * sea que en un movil de 380 px una etiqueta calibrada para 1200 px se queda en
 * cuatro pixeles. Es literalmente el defecto que este archivo viene a corregir,
 * un tamaño mas abajo. `disponer()` mide y recoloca; un `ResizeObserver` lo
 * repite cuando la ventana cambia.
 * ---------------------------------------------------------------------------
 */

import type { CriterioResuelto } from '../api';
import { el } from '../dom';
import type { Traductor } from '../i18n';
import { LAT_MAX, LAT_MIN, MAPA_ALTO, MAPA_ANCHO, TIERRA_FIRME } from './mapa-mundo';

const SVG = 'http://www.w3.org/2000/svg';

function nodo<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string>,
): SVGElementTagNameMap[K] {
  const n = document.createElementNS(SVG, tag);
  for (const [k, v] of Object.entries(attrs)) n.setAttribute(k, v);
  return n;
}

const n = (x: number): string => String(Math.round(x * 100) / 100);

interface Punto {
  readonly x: number;
  readonly y: number;
}

/** Grados decimales, como los guarda `ubicacion` [migracion 0021]. */
interface Coordenada {
  readonly lon: number;
  readonly lat: number;
}

/**
 * Equirectangular, la misma que uso `generar-mapa.mjs` para la silueta. Si una
 * cambia y la otra no, los puertos caen en el mar y nada falla: solo se ve mal.
 */
function proyectar(c: Coordenada): Punto {
  return {
    x: ((c.lon + 180) / 360) * MAPA_ANCHO,
    y: ((LAT_MAX - c.lat) / (LAT_MAX - LAT_MIN)) * MAPA_ALTO,
  };
}

/**
 * El punto corrido al camino corto respecto de una referencia. Ver "la ruta
 * cruza el Pacifico" arriba.
 *
 * Devuelve el punto tal cual cuando no cruza el antimeridiano, que es la
 * mayoria.
 */
function desenrollar(referencia: Punto, punto: Punto): Punto {
  const dx = punto.x - referencia.x;
  if (Math.abs(dx) <= MAPA_ANCHO / 2) return punto;
  return { x: punto.x - Math.sign(dx) * MAPA_ANCHO, y: punto.y };
}

/** La cima del arco, que es lo que decide cuanto sube la curva y por tanto
 *  cuanto alto hay que reservarle al encuadre. */
function alturaDelArco(a: Punto, b: Punto): number {
  const cuerda = Math.hypot(b.x - a.x, b.y - a.y);
  return Math.min(cuerda * 0.2, MAPA_ALTO * 0.3);
}

/**
 * El arco entre dos puntos.
 *
 * Una CUBICA con los dos tiradores a un cuarto y a tres cuartos de la cuerda,
 * los dos a la misma altura. Antes era una cuadratica, que tiene un solo
 * tirador y por tanto un pico: la cubica reparte la curvatura y deja una cima
 * larga y plana, que es como se dibuja una derrota en una carta y lo que hace
 * que el trazo no parezca una diadema.
 *
 * No pretende ser una ortodromica --que en equirectangular no es un arco
 * simple-- y por eso el mapa no lleva escala ni distancias: dice POR DONDE, no
 * CUANTO.
 *
 * `pathLength="1"` no es decorativo: normaliza la longitud del trazo a 1 y con
 * eso el dibujado de entrada se anima en CSS con un `stroke-dasharray: 1`
 * igual para una ruta de 200 km que para una transpacifica, sin medir nada
 * desde JavaScript.
 */
function arco(a: Punto, b: Punto): string {
  // 0.75 es la desviacion de una cubica simetrica con los tiradores a 0.28 y
  // 0.72 en su punto medio: se invierte para que la cima sea exactamente la que
  // `encuadrar` reservo.
  const tirador = alturaDelArco(a, b) / 0.75;
  const c1 = { x: a.x + (b.x - a.x) * 0.28, y: a.y + (b.y - a.y) * 0.28 - tirador };
  const c2 = { x: a.x + (b.x - a.x) * 0.72, y: a.y + (b.y - a.y) * 0.72 - tirador };
  return `M${n(a.x)} ${n(a.y)} C${n(c1.x)} ${n(c1.y)} ${n(c2.x)} ${n(c2.y)} ${n(b.x)} ${n(b.y)}`;
}

/**
 * El punto mas alto del arco y hacia donde apunta ahi.
 *
 * Los dos salen de la cubica de `arco` y no de una aproximacion. Con los
 * tiradores simetricos a 0.28 y 0.72, en t = 0.5 la curva pasa por la cuerda
 * mas tres cuartos del tirador, y su tangente resulta ser PARALELA A LA CUERDA
 * --los terminos perpendiculares se cancelan--. Por eso la punta de flecha se
 * puede orientar con el angulo de la cuerda y cae exactamente sobre la curva.
 */
function cimaDelArco(a: Punto, b: Punto): { punto: Punto; angulo: number } {
  return {
    punto: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 - alturaDelArco(a, b) },
    angulo: (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI,
  };
}

interface Ventana {
  readonly x: number;
  readonly y: number;
  readonly ancho: number;
  readonly alto: number;
}

/**
 * La ventana que enmarca las rutas, con margen y estirada a `proporcion`.
 *
 * El margen es relativo al tamaño de la ruta, no fijo: en una ruta corta un
 * margen fijo de 80 unidades la dejaria perdida en el centro, y en una larga no
 * se notaria. Los minimos evitan que dos puertos vecinos --Shekou y Yantian
 * estan a 30 km-- produzcan una ventana de tres pixeles.
 */
function encuadrar(
  puntos: readonly Punto[],
  proporcion: number,
  /**
   * Sitio que se deja LIBRE ABAJO para las etiquetas, como fraccion del alto.
   *
   * Debajo, que es donde tienen que ir: el arco sale hacia arriba de los dos
   * extremos, asi que encima se le cruzan por delante. Sin esta reserva, el
   * puerto de destino caia en el ultimo cuarto de la ventana, la etiqueta se
   * iba arriba y el arco la atravesaba --se veia en el navegador--.
   *
   * Fraccion y no un valor fijo en unidades de mundo: las etiquetas se miden en
   * pixeles de pantalla, y cuantas unidades de mundo ocupan depende de lo
   * abierta que este la ventana. Una constante que vale para una transpacifica
   * aplasta una ruta de 30 km.
   */
  reservaAbajo: number,
): Ventana {
  const x1 = Math.min(...puntos.map((p) => p.x));
  const x2 = Math.max(...puntos.map((p) => p.x));
  const y1 = Math.min(...puntos.map((p) => p.y));
  const y2 = Math.max(...puntos.map((p) => p.y));

  const margen = Math.max((x2 - x1) * 0.12, (y2 - y1) * 0.25, 26);

  let ancho = Math.max(x2 - x1 + margen * 2, 150);
  let alto = Math.max(y2 - y1 + margen * 2, 90);

  // Estirar --nunca encoger-- hasta la proporcion pedida: encoger dejaria fuera
  // uno de los extremos, que es exactamente el fallo que esto viene a arreglar.
  if (ancho / alto < proporcion) ancho = alto * proporcion;
  else alto = ancho / proporcion;

  // La reserva de etiquetas crece la ventana hacia ABAJO: el borde de arriba se
  // queda donde estaba y lo que se añade sale por debajo del puerto mas bajo.
  const arriba = (y1 + y2) / 2 - alto / 2;
  alto = alto * (1 + reservaAbajo);
  ancho = alto * proporcion;

  // Vertical: la ventana no se sale del mundo si cabe dentro. La silueta esta
  // recortada por arriba y por abajo, y sus vertices quedaron aplastados contra
  // el borde; asomarse mas alla enseña esa linea recta de corte, que se ve como
  // una franja de tierra que no existe. A lo ancho no se hace nada: ahi el mapa
  // se repite a los lados y no tiene bordes.
  let y = arriba;
  if (alto >= MAPA_ALTO) y = (MAPA_ALTO - alto) / 2;
  else y = Math.max(0, Math.min(MAPA_ALTO - alto, y));

  return { x: (x1 + x2) / 2 - ancho / 2, y, ancho, alto };
}

/**
 * Sitio reservado bajo la ruta para las etiquetas, como fraccion del alto de la
 * ventana. Ver `encuadrar`.
 */
const RESERVA_ETIQUETAS = 0.22;

/**
 * Colocar un adorno sabiendo cuantas unidades de mundo mide un pixel y donde
 * ha quedado la ventana. Ver la cabecera y `disponer()`.
 */
type Ajuste = (unidadesPorPixel: number, ventana: Ventana) => void;

export type TamanoMapa = 'banda' | 'documento';

/**
 * La proporcion DE RESERVA de cada tamaño: la que se usa mientras no hay una
 * medida del hueco --jsdom, o el mapa todavia plegado--.
 *
 * En el navegador manda la forma real del elemento, que la decide el
 * `aspect-ratio` de `estilo/mapa.css` y cambia con el ancho de la pantalla.
 * `disponer()` la mide y rehace el encuadre con ella, asi que estos valores no
 * tienen que perseguir a los del CSS: basta con que se parezcan al caso ancho
 * para que el primer pintado no de un salto.
 */
const PROPORCION: Record<TamanoMapa, number> = {
  banda: 4,
  documento: 3,
};

/**
 * Los adornos, en pixeles de pantalla. Ver la cabecera.
 *
 * El GROSOR DEL TRAZO esta aqui y no en el CSS, y tiene motivo. La forma
 * habitual de que una linea de un SVG no engorde al acercarse es
 * `vector-effect: non-scaling-stroke`, y es la que usa la silueta de tierra.
 * En el arco NO se puede: con `non-scaling-stroke`, Chromium deja de aplicar la
 * normalizacion de `pathLength` al patron de guiones, y el dibujado de entrada
 * --que es justo un `stroke-dasharray` sobre esa normalizacion-- se queda a
 * medias. Se veia en el navegador: la ruta de la cotizacion terminaba en mitad
 * del Pacifico, sin llegar a Puerto Quetzal.
 */
const ADORNOS: Record<
  TamanoMapa,
  {
    readonly punto: number;
    readonly halo: number;
    readonly etiqueta: number;
    readonly locode: number;
    readonly trazo: number;
    readonly trazoFuerte: number;
    readonly flecha: number;
  }
> = {
  banda: {
    punto: 4.5,
    halo: 8,
    etiqueta: 13,
    locode: 11,
    trazo: 2.5,
    trazoFuerte: 4,
    flecha: 8,
  },
  documento: {
    punto: 5.5,
    halo: 10,
    etiqueta: 15,
    locode: 12.5,
    trazo: 3.5,
    trazoFuerte: 5,
    flecha: 10,
  },
};

interface PuertoRuta {
  readonly nombre: string;
  readonly pais: string;
  readonly locode: string | null;
}

/**
 * Un punto suelto con coordenada: el aeropuerto del que despega de verdad. [H82]
 *
 * No es un `PuertoRuta`: no tiene pais en la respuesta de la busqueda --viene
 * del itinerario de la tarifa, no del criterio-- y se dibuja con su designador,
 * que es como lo escribe el proveedor.
 */
export interface PuntoSuelto {
  readonly iata: string | null;
  readonly nombre: string;
  readonly latitud: number | null;
  readonly longitud: number | null;
}

/**
 * La coordenada de un puerto del criterio, o `null` si el catalogo no la tiene.
 *
 * ---------------------------------------------------------------------------
 * AQUI HABIA UNA TABLA DE COORDENADAS EN EL CLIENTE
 *
 * `mapa-puertos.ts` guardaba 44 pares lon/lat escritos a mano, indexados por
 * LOCODE, y se documentaba a si mismo como provisional: "su sitio es la tabla
 * `ubicacion` ... cuando la tenga, este archivo se borra entero y `mapa.ts` las
 * lee de `criterio.pol` / `criterio.pod`". Eso es exactamente lo que paso en la
 * migracion 0021, y el archivo ya no existe.
 *
 * Lo que NO cambia es la regla: un puerto sin coordenada no se pinta en un
 * sitio aproximado. El mapa desaparece y en su lugar queda una frase que dice
 * de que puerto falta el dato. Un punto puesto a ojo en el mapa de un cotizador
 * se lee como un dato, y nadie vuelve a comprobarlo [H59].
 *
 * Las dos comprobaciones son por si la API es mas vieja que la pantalla --un
 * despliegue a medias manda el criterio sin las dos columnas nuevas-- y para
 * que un `null` de una sola de las dos no acabe en un punto en el ecuador.
 * ---------------------------------------------------------------------------
 */
function coordenada(puerto: {
  readonly latitud?: number | null;
  readonly longitud?: number | null;
}): Coordenada | null {
  const { latitud, longitud } = puerto;
  if (typeof latitud !== 'number' || typeof longitud !== 'number') return null;
  return { lon: longitud, lat: latitud };
}

/** Una ruta dibujable: dos puertos con coordenada y una clave para resaltarla. */
export interface RutaMapa {
  readonly id: string;
  readonly pol: PuertoRuta;
  readonly pod: PuertoRuta;
  readonly cPol: Coordenada;
  readonly cPod: Coordenada;
}

/**
 * La clave de una ruta: la que casa una fila de la tabla con su trazo.
 *
 * Hoy una busqueda tiene una sola ruta --la API filtra por `pol_id` y `pod_id`,
 * asi que todos sus resultados navegan la misma-- y esta clave sale del
 * criterio. El dia que una pantalla enseñe varias a la vez, lo que cambia es de
 * donde se saca, no el resto.
 */
export function claveRuta(pol: PuertoRuta, pod: PuertoRuta): string {
  return `${pol.locode ?? pol.nombre}->${pod.locode ?? pod.nombre}`;
}

/** La ruta del criterio, o `null` si falta la coordenada de algun puerto. */
function rutaDelCriterio(criterio: CriterioResuelto): RutaMapa | null {
  const cPol = coordenada(criterio.pol);
  const cPod = coordenada(criterio.pod);
  if (cPol === null || cPod === null) return null;
  return {
    id: claveRuta(criterio.pol, criterio.pod),
    pol: criterio.pol,
    pod: criterio.pod,
    cPol,
    cPod,
  };
}

/** Un id irrepetible por mapa: en la pantalla puede haber dos a la vez --el de
 *  la busqueda y el de la cotizacion-- y dos `id` iguales harian que los dos
 *  `use` apunten al primero. */
let secuencia = 0;

export interface Mapa {
  /** Lo que se inserta en la pantalla. En la banda es la caja con su boton de
   *  desplegar; en el documento, la figura a secas. */
  readonly elemento: HTMLElement;
  /** La figura con el SVG dentro. */
  readonly figura: HTMLElement;
  /**
   * Resalta una ruta y apaga las demas, o `null` para dejarlas todas iguales.
   *
   * Con una sola ruta dibujada no hay nada que apagar y lo que hace es
   * encenderla: es el hilo entre la fila que el agente esta mirando y su trazo.
   */
  resaltar: (ruta: string | null) => void;
  /**
   * Insinua el pre-acarreo terrestre de la tarifa que se esta mirando, o lo
   * borra con `null`. [H82]
   *
   * INSINUAR Y NO DIBUJAR. Es un trazo de puntos entre el aeropuerto que declara
   * la tarifa y el que de verdad despega --SZX por carretera hasta HKG-- y no
   * pretende ser la carretera: es equirectangular y no hay carretera recta entre
   * dos puntos de un mapa asi. Lo que dice es QUE HAY UN TRAMO, no por donde va,
   * igual que el arco dice por donde y no cuanto.
   *
   * Vive fuera de `RutaMapa` porque no es de la ruta: la ruta es del criterio y
   * la tienen todas las tarifas de la busqueda, y el pre-acarreo es de UNA
   * tarifa. Se enciende con el mismo gesto que enciende su trazo.
   *
   * No hace nada cuando el punto no trae coordenada: un aeropuerto puesto a ojo
   * en el mapa de un cotizador se lee como un dato [H59]. El tramo se sigue
   * diciendo con palabras en la fila.
   */
  preacarreo: (punto: PuntoSuelto | null) => void;
  /** Vuelve a medir y recolocar los adornos. Se llama sola al montar y al
   *  cambiar el tamaño; a mano cuando el mapa pasa de oculto a visible. */
  disponer: () => void;
}

/**
 * El mapa de una ruta, o `null` si no se puede dibujar honestamente.
 *
 * `null` cuando falta la coordenada de alguno de los dos puertos. No se pinta
 * un punto aproximado: en el mapa de un cotizador se leeria como un dato.
 * Quien llama decide si pinta `mapaSinCoordenadas` o no pinta nada. Ver la nota
 * de `coordenada` mas arriba.
 */
export function mapaRuta(
  criterio: CriterioResuelto,
  tamano: TamanoMapa,
  t: Traductor,
): Mapa | null {
  const ruta = rutaDelCriterio(criterio);
  if (ruta === null) return null;
  return mapaDeRutas([ruta], tamano, t);
}

/**
 * El mapa de N rutas.
 *
 * N y no una porque el dibujo no tiene por que saber cuantas hay: encuadra
 * todos los puntos que le den y pinta un grupo por ruta. Hoy siempre recibe
 * una --ver `claveRuta`--; el resaltado por fila es el mismo codigo tanto si
 * apaga otras rutas como si no hay ninguna que apagar.
 */
export function mapaDeRutas(
  rutas: readonly RutaMapa[],
  tamano: TamanoMapa,
  t: Traductor,
): Mapa {
  const adornos = ADORNOS[tamano];

  // Desenrollado: el origen de la primera ruta es el ancla y todo lo demas se
  // corre al camino corto respecto de ella, para que dos rutas no acaben una a
  // cada lado del lienzo.
  const ancla = proyectar(rutas[0].cPol);
  const trazadas = rutas.map((r) => {
    const pol = desenrollar(ancla, proyectar(r.cPol));
    return { ruta: r, pol, pod: desenrollar(pol, proyectar(r.cPod)) };
  });

  // La cima de cada arco entra en el encuadre: es el punto mas alto del dibujo.
  const puntos: Punto[] = [];
  for (const tr of trazadas) {
    puntos.push(tr.pol, tr.pod);
    const cima = alturaDelArco(tr.pol, tr.pod);
    puntos.push({ x: (tr.pol.x + tr.pod.x) / 2, y: Math.min(tr.pol.y, tr.pod.y) - cima });
  }

  // La primera ventana sale de la proporcion de reserva. En el navegador se
  // recalcula enseguida con la forma medida del hueco; esta es la que vale
  // donde no hay medida --jsdom, o el mapa todavia plegado--.
  let ventana = encuadrar(puntos, PROPORCION[tamano], RESERVA_ETIQUETAS);

  const svg = nodo('svg', {
    viewBox: `${n(ventana.x)} ${n(ventana.y)} ${n(ventana.ancho)} ${n(ventana.alto)}`,
    preserveAspectRatio: 'xMidYMid meet',
    role: 'img',
    // El mapa no aporta ningun dato que no este escrito al lado, asi que su
    // texto alternativo es la ruta y se acaba. Los nombres van dentro en
    // `<text>`, y ahi repiten lo que el resumen ya dice.
    'aria-label': rutas
      .map((r) =>
        t('mapa.alt', {
          pol: `${r.pol.nombre} (${r.pol.pais})`,
          pod: `${r.pod.nombre} (${r.pod.pais})`,
        }),
      )
      .join('. '),
  });

  secuencia += 1;
  const idTierra = `tierra-firme-${String(secuencia)}`;

  const defs = nodo('defs', {});
  defs.appendChild(nodo('path', { id: idTierra, d: TIERRA_FIRME }));
  svg.appendChild(defs);

  // El mar cubre la ventana entera, este donde este. Con holgura a los lados
  // por si el hueco medido no acaba de coincidir con el `viewBox`: ahi el
  // `meet` enseña mundo por fuera, y sin este margen asomaria el fondo de la
  // pagina en vez del mar.
  const mar = nodo('rect', { class: 'mapa-mar' });
  svg.appendChild(mar);

  // La silueta, tres veces: en su sitio y un mundo a cada lado, para que el
  // mapa no se acabe cuando la ventana cruza el antimeridiano.
  for (const desplazamiento of [-MAPA_ANCHO, 0, MAPA_ANCHO]) {
    const uso = nodo('use', { x: String(desplazamiento), class: 'mapa-tierra' });
    // `href` es el atributo actual y `xlink:href` el que entienden los motores
    // viejos. Ponerlos los dos no cuesta nada y evita un mapa en blanco.
    uso.setAttribute('href', `#${idTierra}`);
    uso.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#${idTierra}`);
    svg.appendChild(uso);
  }

  /** Lo que hay que recolocar cuando se sabe cuantas unidades de mundo mide un
   *  pixel y donde ha quedado la ventana. Ver la cabecera. */
  const ajustes: Ajuste[] = [];

  /** El pre-acarreo que se esta insinuando ahora, y la escala con la que se
   *  dibujo. Los dos hacen falta para rehacerlo al redimensionar. */
  let puntoPreacarreo: PuntoSuelto | null = null;
  let unidadesPorPixelActual = 1;
  const gPreacarreo = nodo('g', { class: 'mapa-preacarreo' });
  const grupos = new Map<string, SVGGElement>();

  for (const tr of trazadas) {
    const g = nodo('g', { class: 'mapa-ruta-grupo' });
    g.dataset.ruta = tr.ruta.id;
    g.appendChild(
      nodo('path', {
        d: arco(tr.pol, tr.pod),
        class: 'mapa-ruta',
        // Ver `arco`: normaliza el trazo para que el dibujado de entrada se
        // anime en CSS sin medir la longitud real.
        pathLength: '1',
      }),
    );
    g.appendChild(flecha(tr.pol, tr.pod, adornos, ajustes));
    g.appendChild(marca(tr.ruta.pol, tr.pol, tr.pod, 'pol', adornos, ajustes));
    g.appendChild(marca(tr.ruta.pod, tr.pod, tr.pol, 'pod', adornos, ajustes));
    grupos.set(tr.ruta.id, g);
    svg.appendChild(g);
  }

  // Encima de las rutas: es un trazo fino y quedaria por debajo del arco.
  svg.appendChild(gPreacarreo);

  const figura = el('figure', { clase: `mapa mapa-${tamano}` }, [svg]);

  // El grosor del trazo, tambien en pixeles de pantalla. Va como variable de
  // CSS y no como atributo para que el resaltado siga siendo una clase --dos
  // grosores, uno normal y uno encendido-- y no haya que repintar el arco.
  ajustes.push((u) => {
    figura.style.setProperty('--mapa-trazo', n(adornos.trazo * u));
    figura.style.setProperty('--mapa-trazo-fuerte', n(adornos.trazoFuerte * u));
  });

  /**
   * Medir el hueco y rehacer el encuadre y los adornos con lo medido.
   *
   * LA FORMA DEL HUECO LA MANDA EL CSS Y EL ENCUADRE LA SIGUE. Antes el
   * `viewBox` se calculaba con una proporcion fija por tamaño y el CSS la
   * cambiaba en pantalla estrecha; el `meet` no recortaba nada, pero enseñaba
   * mundo de mas, y en un movil eso dejaba la ruta como un arquito perdido en
   * mitad del oceano --con la etiqueta del destino cruzada por encima--. Se vio
   * en el navegador a 390 px.
   *
   * Sin medida --jsdom, o el mapa plegado, que mide cero-- se queda la ventana
   * de reserva y la escala de un lienzo de 1000 px, que es con la que estan
   * calibradas las constantes.
   */
  const disponer = (): void => {
    const caja = figura.getBoundingClientRect?.();
    const anchoReal = caja?.width ?? 0;
    const altoReal = caja?.height ?? 0;

    if (anchoReal > 0 && altoReal > 0) {
      ventana = encuadrar(puntos, anchoReal / altoReal, RESERVA_ETIQUETAS);
      svg.setAttribute(
        'viewBox',
        `${n(ventana.x)} ${n(ventana.y)} ${n(ventana.ancho)} ${n(ventana.alto)}`,
      );
    }

    mar.setAttribute('x', n(ventana.x - ventana.ancho));
    mar.setAttribute('y', n(ventana.y - ventana.alto));
    mar.setAttribute('width', n(ventana.ancho * 3));
    mar.setAttribute('height', n(ventana.alto * 3));

    const unidadesPorPixel =
      anchoReal > 0 ? ventana.ancho / anchoReal : ventana.ancho / 1000;
    unidadesPorPixelActual = unidadesPorPixel;
    for (const ajuste of ajustes) ajuste(unidadesPorPixel, ventana);
    // El pre-acarreo se rehace con la escala nueva. Va al final: sus adornos se
    // miden en pixeles igual que los demas.
    dibujarPreacarreo();
  };
  disponer();

  // El navegador recoloca al cambiar el ancho; jsdom no trae `ResizeObserver` y
  // ahi no hace falta, porque nada se redimensiona.
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(disponer).observe(figura);
  }

  const resaltar = (ruta: string | null): void => {
    for (const [id, g] of grupos) {
      g.classList.toggle('mapa-ruta-resaltada', ruta !== null && id === ruta);
      g.classList.toggle('mapa-ruta-apagada', ruta !== null && id !== ruta);
    }
  };

  const preacarreo = (punto: PuntoSuelto | null): void => {
    puntoPreacarreo = punto;
    dibujarPreacarreo();
  };

  return {
    elemento: tamano === 'banda' ? plegable(figura, t) : figura,
    figura,
    resaltar,
    preacarreo,
    disponer,
  };

  /**
   * El trazo de puntos entre el aeropuerto declarado y el de salida real.
   *
   * Se rehace entero en cada llamada en vez de esconderse y reaparecer: la
   * geometria depende de la ventana, que cambia al redimensionar, y guardar el
   * nodo obligaria a recolocarlo desde `disponer` con una tercera copia de las
   * mismas cuentas.
   *
   * Ancla el desenrollado en el ORIGEN de la primera ruta, igual que el resto
   * del dibujo: si no, un aeropuerto de salida al otro lado del antimeridiano
   * se iria a la punta contraria del lienzo.
   */
  function dibujarPreacarreo(): void {
    gPreacarreo.replaceChildren();
    const punto = puntoPreacarreo;
    if (punto === null) return;
    const destino = coordenada(punto);
    if (destino === null) return;

    const desde = trazadas[0].pol;
    const hasta = desenrollar(desde, proyectar(destino));

    gPreacarreo.appendChild(
      nodo('line', {
        x1: n(desde.x),
        y1: n(desde.y),
        x2: n(hasta.x),
        y2: n(hasta.y),
        class: 'mapa-preacarreo-trazo',
      }),
    );
    gPreacarreo.appendChild(
      nodo('circle', {
        cx: n(hasta.x),
        cy: n(hasta.y),
        r: n(adornos.punto * unidadesPorPixelActual),
        class: 'mapa-preacarreo-punto',
      }),
    );
    // LA ETIQUETA VA DEBAJO, Y NO ES UNA PREFERENCIA.
    //
    // Los nombres de los dos puertos se pintan a la ALTURA de su punto y hacia
    // fuera [ver `marca`], asi que el hueco libre alrededor del origen es el de
    // arriba y el de abajo. Se elige abajo porque arriba es por donde sale el
    // arco.
    //
    // Hace falta porque el trazo puede no verse: SZX y HKG estan a 40 km, y en
    // un encuadre transpacifico eso es medio pixel. El tramo se dibuja igual
    // --exagerarlo seria inventar geografia en un mapa que se lee como un
    // dato-- y lo que queda visible es el punto ambar con su designador al
    // lado. Quien mire el mapa ve que hay OTRO aeropuerto pegado al de origen;
    // lo que ese aeropuerto significa lo dice la fila, con palabras.
    const etiqueta = nodo('text', {
      x: n(hasta.x),
      // `y` es la LINEA BASE, asi que el texto sube desde ahi: el hueco tiene
      // que contar una altura de fuente entera mas el radio del punto, o el
      // designador acaba a la altura del nombre del aeropuerto de origen y se
      // cruzan. Se calcula y no se fija en pixeles porque el tamaño de los
      // adornos se mide en pantalla y se convierte a mundo [ver la cabecera].
      y: n(hasta.y + (adornos.halo + adornos.locode * 1.8) * unidadesPorPixelActual),
      class: 'mapa-preacarreo-etiqueta',
      'text-anchor': 'middle',
      'font-size': n(adornos.locode * unidadesPorPixelActual),
    });
    etiqueta.textContent = punto.iata ?? punto.nombre;
    gPreacarreo.appendChild(etiqueta);
  }
}

/**
 * La caja de la banda: el mapa y, SOLO EN PANTALLA ESTRECHA, un boton para
 * desplegarlo.
 *
 * En ancho el boton no existe --`display: none`-- y el mapa esta siempre a la
 * vista. En estrecho el mapa arranca plegado: la alternativa era dejar la banda
 * puesta y encogerla hasta que cabe, y una banda de 4.5:1 en 380 px son 84 px
 * de alto, o sea un mapa que no se lee y que ademas empuja la tabla fuera de la
 * pantalla. Plegado ocupa una linea, y desplegado se abre con una forma mas
 * cuadrada --la del CSS-- en la que si se lee.
 */
function plegable(figura: HTMLElement, t: Traductor): HTMLElement {
  secuencia += 1;
  const idFigura = `mapa-banda-${String(secuencia)}`;
  figura.id = idFigura;

  const caja = el('div', { clase: 'mapa-caja' });
  caja.dataset.abierto = 'false';

  const boton = el('button', {
    clase: 'boton boton-plano boton-menor mapa-alternar',
    texto: t('mapa.ver'),
    attrs: { type: 'button', 'aria-expanded': 'false', 'aria-controls': idFigura },
    onClick: () => {
      const abierto = caja.dataset.abierto !== 'true';
      caja.dataset.abierto = abierto ? 'true' : 'false';
      boton.setAttribute('aria-expanded', abierto ? 'true' : 'false');
      boton.textContent = abierto ? t('mapa.ocultar') : t('mapa.ver');
    },
  });

  caja.appendChild(boton);
  caja.appendChild(figura);
  return caja;
}

/**
 * La punta de flecha en la cima del arco: por donde va la carga.
 *
 * QUIEN DICE LA DIRECCION. Las etiquetas llevan nombre y LOCODE y ya no "Carga"
 * y "Descarga": esas dos palabras hacian la etiqueta un 60% mas larga y en un
 * movil no cabia --se veia deslizarse hasta despegarse de su puerto--. La
 * direccion la dice esto, que es como se dice en cualquier carta de ruta y no
 * ocupa ancho. Para quien no ve el dibujo, el `aria-label` sigue diciendo "de X
 * a Y", que es la misma informacion en palabras.
 *
 * En la cima y no en el extremo: en el extremo se pelearia con el punto del
 * puerto, que es lo que hay que mirar ahi.
 */
function flecha(
  pol: Punto,
  pod: Punto,
  adornos: (typeof ADORNOS)[TamanoMapa],
  ajustes: Ajuste[],
): SVGPathElement {
  const { punto, angulo } = cimaDelArco(pol, pod);
  const p = nodo('path', { class: 'mapa-flecha' });

  ajustes.push((u) => {
    const l = adornos.flecha * u;
    // Un triangulo apuntando al eje +x, que la rotacion lleva a la tangente.
    p.setAttribute(
      'd',
      `M${n(-l * 0.5)} ${n(-l * 0.62)}L${n(l * 0.75)} 0L${n(-l * 0.5)} ${n(l * 0.62)}Z`,
    );
    p.setAttribute(
      'transform',
      `translate(${n(punto.x)} ${n(punto.y)}) rotate(${n(angulo)})`,
    );
  });

  return p;
}

/**
 * Un extremo: el punto y su etiqueta.
 *
 * Los dos tamaños etiquetan. Antes solo lo hacia el grande y el pequeño dejaba
 * dos puntos sin nombre, que es medio mapa: quien no reconoce el LOCODE tampoco
 * reconoce el punto.
 *
 * Nada de esto se dimensiona aqui: se apunta en `ajustes` y lo coloca
 * `disponer()` cuando sabe cuanto mide un pixel. Ver la cabecera.
 */
function marca(
  puerto: PuertoRuta,
  punto: Punto,
  /** El otro extremo de la ruta: la etiqueta se aparta de el. Ver abajo. */
  otro: Punto,
  cual: 'pol' | 'pod',
  adornos: (typeof ADORNOS)[TamanoMapa],
  ajustes: Ajuste[],
): SVGGElement {
  const g = nodo('g', { class: `mapa-marca mapa-marca-${cual}` });

  // El halo va debajo del punto y en el color del papel: sobre una costa, un
  // punto sin separacion se confunde con la tierra.
  const halo = nodo('circle', { cx: n(punto.x), cy: n(punto.y), class: 'mapa-halo' });
  const centro = nodo('circle', { cx: n(punto.x), cy: n(punto.y), class: 'mapa-punto' });
  g.appendChild(halo);
  g.appendChild(centro);

  // Vertical: siempre DEBAJO del punto. El arco sale hacia arriba de los dos
  // extremos, asi que encima se le cruza por delante. Que quepan lo garantiza
  // `encuadrar`, que reserva el hueco de abajo; ver `reservaAbajo`.
  // Nombre y pais, y debajo el LOCODE. Sin "Carga" ni "Descarga" delante: eso
  // lo dice la punta de flecha, y esas dos palabras hacian la etiqueta tan
  // larga que en un movil no cabia al lado de su puerto. Ver `flecha`.
  const etiqueta = nodo('text', { class: 'mapa-etiqueta' });
  etiqueta.textContent = `${puerto.nombre} (${puerto.pais})`;
  g.appendChild(etiqueta);

  // [H59] El LOCODE se pinta si existe. Si no, la etiqueta se queda con el
  // nombre: una linea vacia debajo se leeria como un codigo que no se pudo
  // cargar.
  const codigo = puerto.locode === null ? null : nodo('text', { class: 'mapa-locode' });
  if (codigo !== null) {
    codigo.textContent = puerto.locode;
    g.appendChild(codigo);
  }

  const textos = [
    [etiqueta, adornos.etiqueta, 18],
    [codigo, adornos.locode, 34],
  ] as const;

  ajustes.push((u, ventana) => {
    halo.setAttribute('r', n(adornos.halo * u));
    centro.setAttribute('r', n(adornos.punto * u));

    // Horizontal: LA ETIQUETA SE APARTA DEL OTRO PUERTO.
    //
    // El arco viaja entre los dos extremos, asi que el lado que mira al otro
    // puerto es justo por donde pasa la curva: en Shanghai -> Valparaiso el
    // trazo cruzaba el nombre por la mitad --se vio en el navegador--. Hacia
    // fuera, en cambio, no hay nada que atravesar.
    //
    // Salvo que ahi no quepa: pegada al canto de la ventana se cortaria, y
    // entonces vuelve hacia dentro. El ancho se ESTIMA de la cadena --0.62 del
    // cuerpo por caracter, medido sobre "Descarga · Puerto Quetzal (GT)" en la
    // pila de fuentes del sistema, con holgura-- en vez de medirlo: medir
    // obliga a estar en un navegador con `getBBox`, y esta decision tiene que
    // salir igual en las pruebas.
    const anchoTexto = Math.max(
      ...textos.map(([nodoTexto, cuerpo]) =>
        nodoTexto === null ? 0 : (nodoTexto.textContent?.length ?? 0) * cuerpo * 0.62 * u,
      ),
    );
    const hueco = 14 * u;
    const derechaCabe = punto.x + hueco + anchoTexto <= ventana.x + ventana.ancho;
    const izquierdaCabe = punto.x - hueco - anchoTexto >= ventana.x;

    // Lado preferido: el contrario al otro puerto. Solo se cambia si ahi no
    // cabe y en el otro si. Y si no cabe en ninguno --un nombre largo en un
    // mapa de movil-- se centra BAJO su punto: pegada al canto se despegaria
    // del puerto que nombra, que es lo unico que no puede pasar.
    let anclaje: 'start' | 'middle' | 'end';
    if (!derechaCabe && !izquierdaCabe) anclaje = 'middle';
    else {
      let aLaDerecha = punto.x >= otro.x;
      if (aLaDerecha && !derechaCabe) aLaDerecha = false;
      else if (!aLaDerecha && !izquierdaCabe) aLaDerecha = true;
      anclaje = aLaDerecha ? 'start' : 'end';
    }

    // Y aun asi se arrima al canto: el ancho es una estimacion, y por unos
    // pocos pixeles no vale la pena cambiar de lado --se veria saltar al
    // redimensionar--. Se desliza lo justo para entrar.
    const izquierda = ventana.x;
    const derecha = ventana.x + ventana.ancho;
    const x =
      anclaje === 'start'
        ? Math.min(punto.x + 11 * u, derecha - anchoTexto)
        : anclaje === 'end'
          ? Math.max(punto.x - 11 * u, izquierda + anchoTexto)
          : Math.min(
              Math.max(punto.x, izquierda + anchoTexto / 2),
              derecha - anchoTexto / 2,
            );

    for (const [nodoTexto, cuerpo, dy] of textos) {
      if (nodoTexto === null) continue;
      nodoTexto.setAttribute('text-anchor', anclaje);
      nodoTexto.setAttribute('x', n(x));
      nodoTexto.setAttribute('y', n(punto.y + dy * u));
      nodoTexto.setAttribute('font-size', n(cuerpo * u));
      // El contorno del color del papel, tambien en pixeles: es lo que despega
      // el texto de la costa que tenga debajo.
      nodoTexto.setAttribute('stroke-width', n(3 * u));
    }
  });

  return g;
}

/**
 * Lo que se pinta cuando falta la coordenada de un puerto.
 *
 * Dice CUAL falta. "No se puede dibujar el mapa" a secas manda a mirar la
 * conexion o el navegador; con el nombre del puerto se entiende que es el
 * catalogo el que no tiene el dato, que es lo que pasa.
 */
export function mapaSinCoordenadas(
  criterio: CriterioResuelto,
  t: Traductor,
): HTMLElement {
  const faltan = [
    coordenada(criterio.pol) === null ? criterio.pol.nombre : null,
    coordenada(criterio.pod) === null ? criterio.pod.nombre : null,
  ].filter((x): x is string => x !== null);

  return el('p', {
    clase: 'mapa-ausente tenue',
    texto: t('mapa.sinCoordenadas', { puertos: faltan.join(', ') }),
  });
}
